import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SecurityTab = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev?.[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z\d]/?.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: 'text-error' };
    if (strength <= 3) return { strength, label: 'Fair', color: 'text-warning' };
    if (strength <= 4) return { strength, label: 'Good', color: 'text-primary' };
    return { strength, label: 'Strong', color: 'text-success' };
  };

  const passwordStrength = getPasswordStrength(formData?.newPassword);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData?.newPassword !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData?.currentPassword === formData?.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');

    try {
      // Update password using Supabase auth
      const { error } = await supabase?.auth?.updateUser({
        password: formData?.newPassword,
      });

      if (error) {
        setErrors({ general: error?.message || 'Failed to update password' });
      } else {
        setSuccessMessage('Password updated successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {errors?.general && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-error/10 border border-error/20">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}

      {/* Password Change Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Change Password</h3>
          <div className="space-y-4">
            {/* Current Password */}
            <div className="relative">
              <Input
                label="Current Password"
                type={showPasswords?.current ? 'text' : 'password'}
                name="currentPassword"
                placeholder="Enter your current password"
                value={formData?.currentPassword}
                onChange={handleChange}
                error={errors?.currentPassword}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-[38px] p-2 rounded-lg hover:bg-muted transition-colors duration-250"
              >
                <Icon
                  name={showPasswords?.current ? 'EyeOff' : 'Eye'}
                  size={18}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <Input
                label="New Password"
                type={showPasswords?.new ? 'text' : 'password'}
                name="newPassword"
                placeholder="Enter your new password"
                value={formData?.newPassword}
                onChange={handleChange}
                error={errors?.newPassword}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-[38px] p-2 rounded-lg hover:bg-muted transition-colors duration-250"
              >
                <Icon
                  name={showPasswords?.new ? 'EyeOff' : 'Eye'}
                  size={18}
                  color="var(--color-muted-foreground)"
                />
              </button>
              {/* Password Strength Indicator */}
              {formData?.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Password Strength:</span>
                    <span className={`text-xs font-medium ${passwordStrength?.color}`}>
                      {passwordStrength?.label}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        passwordStrength?.strength <= 2
                          ? 'bg-error'
                          : passwordStrength?.strength <= 3
                          ? 'bg-warning'
                          : passwordStrength?.strength <= 4
                          ? 'bg-primary' :'bg-success'
                      }`}
                      style={{ width: `${(passwordStrength?.strength / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <Input
                label="Confirm New Password"
                type={showPasswords?.confirm ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm your new password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                error={errors?.confirmPassword}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-[38px] p-2 rounded-lg hover:bg-muted transition-colors duration-250"
              >
                <Icon
                  name={showPasswords?.confirm ? 'EyeOff' : 'Eye'}
                  size={18}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="bg-muted/50 rounded-lg p-4 border border-border">
          <h4 className="text-sm font-medium text-foreground mb-2">Password Requirements:</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li className="flex items-center gap-2">
              <Icon
                name={formData?.newPassword?.length >= 8 ? 'CheckCircle' : 'Circle'}
                size={14}
                color={formData?.newPassword?.length >= 8 ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
              At least 8 characters long
            </li>
            <li className="flex items-center gap-2">
              <Icon
                name={/[A-Z]/?.test(formData?.newPassword) ? 'CheckCircle' : 'Circle'}
                size={14}
                color={/[A-Z]/?.test(formData?.newPassword) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
              Contains uppercase letter
            </li>
            <li className="flex items-center gap-2">
              <Icon
                name={/[a-z]/?.test(formData?.newPassword) ? 'CheckCircle' : 'Circle'}
                size={14}
                color={/[a-z]/?.test(formData?.newPassword) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
              Contains lowercase letter
            </li>
            <li className="flex items-center gap-2">
              <Icon
                name={/\d/?.test(formData?.newPassword) ? 'CheckCircle' : 'Circle'}
                size={14}
                color={/\d/?.test(formData?.newPassword) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
              Contains number
            </li>
            <li className="flex items-center gap-2">
              <Icon
                name={/[^a-zA-Z\d]/?.test(formData?.newPassword) ? 'CheckCircle' : 'Circle'}
                size={14}
                color={/[^a-zA-Z\d]/?.test(formData?.newPassword) ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
              Contains special character (recommended)
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-6 border-t border-border">
          <Button
            type="submit"
            variant="default"
            loading={isLoading}
            iconName="Lock"
          >
            Update Password
          </Button>
          <Button
            type="button"
            onClick={handleCancel}
            variant="outline"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SecurityTab;